import React, { useEffect, useState } from "react";
import { Bell, Trash, Plus, Snowflake, Drop, TrendDown } from "@phosphor-icons/react";
import { toast } from "sonner";
import RegionSelect from "@/components/RegionSelect";
import { fetchRegions, listAlerts, createAlert, deleteAlert } from "@/lib/api";

const RULES = [
  { id: "score_below", label: "Score fällt unter…", icon: TrendDown, unit: "Punkte", default: 40, tone: "#FF5500" },
  { id: "frost_within_72h", label: "Frost in 72 h", icon: Snowflake, unit: "", default: 0, tone: "#4DA3FF" },
  { id: "irrigation_urgent", label: "Bewässerung dringend", icon: Drop, unit: "", default: 0, tone: "#FFC24B" },
];

export default function Alerts() {
  const [regions, setRegions] = useState([]);
  const [alerts, setAlerts] = useState([]);
  const [regionId, setRegionId] = useState("");
  const [ruleId, setRuleId] = useState("score_below");
  const [threshold, setThreshold] = useState(40);

  const refresh = async () => setAlerts(await listAlerts());

  useEffect(() => {
    (async () => {
      const [rs, al] = await Promise.all([fetchRegions(), listAlerts()]);
      setRegions(rs);
      setAlerts(al);
      if (rs[0]) setRegionId(rs[0].id);
    })();
  }, []);

  const onCreate = async (e) => {
    e.preventDefault();
    if (!regionId) return;
    const r = await createAlert({ region_id: regionId, rule: ruleId, threshold: Number(threshold) || 0 });
    if (r.ok) {
      toast.success("Alert eingerichtet");
      refresh();
    } else toast.error("Konnte Alert nicht anlegen");
  };

  const onDelete = async (id) => {
    await deleteAlert(id);
    toast.success("Alert gelöscht");
    refresh();
  };

  return (
    <div className="space-y-5" data-testid="alerts-root">
      <header>
        <div className="eid-label">Frühwarnung · E-Mail</div>
        <h1 className="text-3xl md:text-4xl font-medium tracking-tight mt-1">Meine Alerts</h1>
        <p className="text-sm text-zinc-400 mt-1 max-w-2xl">
          Bodenblick schickt dir automatisch eine Mail, wenn eine deiner Regeln eintrifft.
          Wir prüfen einmal täglich nach dem morgendlichen Pipeline-Lauf. Max. 1 Mail pro Regel in 24 h.
        </p>
      </header>

      <form onSubmit={onCreate} className="eid-card p-5 space-y-3" data-testid="alert-form">
        <h2 className="text-lg font-medium">Neuen Alert einrichten</h2>
        <div className="grid grid-cols-1 md:grid-cols-2 gap-3">
          <div>
            <label className="eid-label">Region</label>
            <RegionSelect regions={regions} value={regionId} onChange={setRegionId} testid="alert-region-select" />
          </div>
          <div>
            <label className="eid-label">Regel</label>
            <div className="flex flex-wrap gap-2 mt-1">
              {RULES.map((r) => {
                const Icon = r.icon;
                const active = r.id === ruleId;
                return (
                  <button
                    type="button"
                    key={r.id}
                    onClick={() => { setRuleId(r.id); setThreshold(r.default); }}
                    data-testid={`alert-rule-${r.id}`}
                    className={`inline-flex items-center gap-1.5 px-3 py-2 rounded-full border text-sm transition-colors ${
                      active ? "text-white" : "bg-[#121212] border-white/10 hover:bg-white/5"
                    }`}
                    style={active ? { background: r.tone, borderColor: r.tone } : {}}
                  >
                    <Icon size={14} /> {r.label}
                  </button>
                );
              })}
            </div>
          </div>
        </div>
        {ruleId === "score_below" && (
          <div className="flex items-end gap-3">
            <div>
              <label className="eid-label">Schwelle</label>
              <input
                type="number" min={0} max={100}
                value={threshold}
                onChange={(e) => setThreshold(e.target.value)}
                data-testid="alert-threshold-input"
                className="w-24 border border-white/10 rounded-sm px-3 py-2 text-sm font-mono"
              />
            </div>
            <span className="text-xs text-zinc-400 pb-2">Punkte</span>
          </div>
        )}
        <button
          type="submit"
          data-testid="alert-create-btn"
          className="inline-flex items-center gap-1.5 bg-[#00FA9A] text-black hover:bg-[#33FBAE] rounded-full px-4 py-2 text-sm font-medium"
        >
          <Plus size={14} /> Alert erstellen
        </button>
      </form>

      <section className="space-y-2" data-testid="alerts-list">
        <h2 className="text-lg font-medium flex items-center gap-2">
          <Bell size={16} /> Aktive Alerts ({alerts.length})
        </h2>
        {alerts.length === 0 ? (
          <div className="eid-card p-5 text-sm text-zinc-400">
            Noch keine Alerts. Leg dir oben einen an — z. B. „Score unter 40 für deine Heim-Region".
          </div>
        ) : (
          alerts.map((a) => {
            const region = regions.find((r) => r.id === a.region_id);
            const rule = RULES.find((r) => r.id === a.rule);
            const Icon = rule?.icon || Bell;
            return (
              <div key={a.id} className="eid-card p-4 flex items-center gap-3"
                   data-testid={`alert-item-${a.id}`}
                   style={{ borderLeft: `3px solid ${rule?.tone || "#8A8A93"}` }}>
                <span className="p-2 rounded-sm" style={{ background: (rule?.tone || "#8A8A93") + "22", color: rule?.tone }}>
                  <Icon size={18} />
                </span>
                <div className="flex-1 min-w-0">
                  <div className="text-sm font-medium">{region?.label || a.region_id}</div>
                  <div className="text-xs text-zinc-400">
                    {rule?.label}{a.rule === "score_below" ? ` ${a.threshold} Punkte` : ""}
                    {a.last_fired_at ? ` · zuletzt ausgelöst ${new Date(a.last_fired_at).toLocaleDateString("de-DE")}` : ""}
                  </div>
                </div>
                <button
                  onClick={() => onDelete(a.id)}
                  className="text-zinc-500 hover:text-[#FF7A45] p-1.5"
                  data-testid={`alert-delete-${a.id}`}
                  aria-label="Alert löschen"
                >
                  <Trash size={16} />
                </button>
              </div>
            );
          })
        )}
      </section>
    </div>
  );
}
